(() => {
class CartShippingEstimator extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.accordionAnimation = null;
    this.isClosing = false;
    this.handleAddressInput = this.handleAddressInput.bind(this);
    this.handleSummaryClick = this.handleSummaryClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCountryChange = this.handleCountryChange.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.form = this.querySelector('form');
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.accordionContent = this.querySelector('[data-shipping-accordion-content]');
    this.country = this.querySelector('[data-shipping-country]');
    this.province = this.querySelector('[data-shipping-province]');
    this.provinceContainer = this.querySelector('[data-shipping-province-container]');
    this.city = this.querySelector('[data-shipping-city]');
    this.zip = this.querySelector('[data-shipping-zip]');
    this.button = this.querySelector('button[type="submit"]');
    this.buttonLabel = this.querySelector('[data-shipping-button-label]');
    this.status = this.querySelector('[data-shipping-status]');
    this.error = this.querySelector('[data-shipping-error]');
    this.results = this.querySelector('[data-shipping-results]');
    this.resultsTitle = this.querySelector('[data-shipping-results-title]');
    this.rates = this.querySelector('[data-shipping-rates]');

    if (
      !this.form ||
      !this.details ||
      !this.summary ||
      !this.accordionContent ||
      !this.country ||
      !this.province ||
      !this.city ||
      !this.button
    ) {
      return;
    }

    this.defaultButtonLabel = this.buttonLabel.textContent;
    this.summary.setAttribute('aria-expanded', String(this.details.open));
    this.summary.addEventListener('click', this.handleSummaryClick);
    this.form.addEventListener('submit', this.handleSubmit);
    this.form.addEventListener('input', this.handleAddressInput);
    this.country.addEventListener('change', this.handleCountryChange);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.abortController?.abort();
        this.setLoading(false);
        this.clearFeedback();
      });
    }
    this.handleCountryChange();
    this.initialized = true;
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.form?.removeEventListener('input', this.handleAddressInput);
    this.summary?.removeEventListener('click', this.handleSummaryClick);
    this.country?.removeEventListener('change', this.handleCountryChange);
    this.cartUpdateUnsubscriber?.();
    this.abortController?.abort();
    this.accordionAnimation?.cancel();
    this.initialized = false;
  }

  handleSummaryClick(event) {
    event.preventDefault();

    const wasOpen = this.details.open;
    const shouldOpen = !wasOpen || this.isClosing;
    const startHeight = wasOpen ? this.accordionContent.getBoundingClientRect().height : 0;
    const computedOpacity = wasOpen ? Number.parseFloat(getComputedStyle(this.accordionContent).opacity) : 0;
    const startOpacity = Number.isFinite(computedOpacity) ? computedOpacity : Number(wasOpen);

    this.accordionAnimation?.cancel();

    if (shouldOpen) {
      this.details.open = true;
    }

    this.isClosing = !shouldOpen;
    this.summary.setAttribute('aria-expanded', String(shouldOpen));

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !this.accordionContent.animate) {
      this.details.open = shouldOpen;
      this.isClosing = false;
      return;
    }

    const endHeight = shouldOpen ? this.accordionContent.scrollHeight : 0;
    const animation = this.accordionContent.animate(
      [
        { height: `${startHeight}px`, opacity: startOpacity },
        { height: `${endHeight}px`, opacity: shouldOpen ? 1 : 0 },
      ],
      {
        duration: 250,
        easing: 'ease',
        fill: 'both',
      }
    );

    this.accordionAnimation = animation;
    animation.onfinish = () => {
      if (this.accordionAnimation !== animation) return;

      if (!shouldOpen) {
        this.details.open = false;
      }

      this.isClosing = false;
      this.summary.setAttribute('aria-expanded', String(this.details.open));
      animation.cancel();
      this.accordionAnimation = null;
    };
  }

  handleCountryChange() {
    this.handleAddressInput();
    const selectedOption = this.country.options[this.country.selectedIndex];
    let provinces = [];

    try {
      provinces = JSON.parse(selectedOption?.dataset.provinces || '[]');
    } catch (error) {
      provinces = [];
    }

    this.province.replaceChildren();

    provinces.forEach(([value, label]) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      this.province.appendChild(option);
    });

    this.provinceContainer.hidden = provinces.length === 0;
  }

  handleAddressInput() {
    this.abortController?.abort();
    this.setLoading(false);
    this.clearFeedback();
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.clearFeedback();
    this.setLoading(true);

    const params = new URLSearchParams();
    params.set('shipping_address[country]', this.country.value);

    if (!this.provinceContainer.hidden && this.province.value) {
      params.set('shipping_address[province]', this.province.value);
    }

    if (this.city.value.trim()) {
      params.set('shipping_address[city]', this.city.value.trim());
    }

    if (this.zip.value.trim()) {
      params.set('shipping_address[zip]', this.zip.value.trim());
    }

    try {
      const [rates, cart] = await Promise.all([
        this.fetchRates(params, controller.signal),
        this.fetchCart(controller.signal),
      ]);
      this.activeCurrency = cart?.currency || this.dataset.currency;
      this.renderRates(rates);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.showError();
      }
    } finally {
      if (this.abortController === controller && !controller.signal.aborted) {
        this.setLoading(false);
      }
    }
  }

  async fetchRates(params, signal) {
    const rootUrl = this.dataset.rootUrl.endsWith('/') ? this.dataset.rootUrl : `${this.dataset.rootUrl}/`;
    const query = params.toString();
    const prepareResponse = await fetch(`${rootUrl}cart/prepare_shipping_rates.json?${query}`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      signal,
    });

    if (!prepareResponse.ok) throw new Error('Unable to prepare shipping rates');

    for (let attempt = 0; attempt < 15; attempt += 1) {
      await this.wait(800, signal);

      const ratesResponse = await fetch(`${rootUrl}cart/async_shipping_rates.json?${query}`, {
        headers: { Accept: 'application/json' },
        signal,
      });

      if (!ratesResponse.ok) throw new Error('Unable to retrieve shipping rates');

      const payload = await ratesResponse.json();
      if (payload !== null) return payload.shipping_rates || [];
    }

    throw new Error('Shipping rate calculation timed out');
  }

  async fetchCart(signal) {
    const rootUrl = this.dataset.rootUrl.endsWith('/') ? this.dataset.rootUrl : `${this.dataset.rootUrl}/`;

    try {
      const response = await fetch(`${rootUrl}cart.js`, {
        headers: { Accept: 'application/json' },
        signal,
      });

      if (!response.ok) return null;
      return response.json();
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return null;
    }
  }

  wait(duration, signal) {
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Request aborted', 'AbortError'));
        return;
      }

      const handleAbort = () => {
        window.clearTimeout(timeout);
        reject(new DOMException('Request aborted', 'AbortError'));
      };

      const timeout = window.setTimeout(() => {
        signal.removeEventListener('abort', handleAbort);
        resolve();
      }, duration);

      signal.addEventListener('abort', handleAbort, { once: true });
    });
  }

  renderRates(rates) {
    this.status.textContent = '';
    this.rates.replaceChildren();

    if (rates.length === 0) {
      this.status.textContent = this.dataset.noRatesText;
      return;
    }

    rates.forEach((rate) => {
      const item = document.createElement('li');
      const name = document.createElement('span');
      const price = document.createElement('span');

      item.className = 'cart-shipping-estimator__rate';
      name.textContent = rate.name;
      price.className = 'cart-shipping-estimator__rate-price';
      price.textContent = this.formatRatePrice(rate);
      item.append(name, price);
      this.rates.appendChild(item);
    });

    this.resultsTitle.textContent = this.dataset.resultsTitle;
    this.results.hidden = false;
  }

  formatRatePrice(rate) {
    const amount = Number.parseFloat(rate.price);
    const sourceCurrency = rate.currency || this.dataset.shopCurrency || this.dataset.currency;
    const targetCurrency = this.activeCurrency || this.dataset.currency;

    if (!Number.isFinite(amount) || !sourceCurrency || !targetCurrency || sourceCurrency === targetCurrency) {
      return this.formatPrice(rate.price, targetCurrency || sourceCurrency);
    }

    const convertedAmount = this.convertPrice(amount, sourceCurrency, targetCurrency);
    if (convertedAmount === null) return this.formatPrice(rate.price, sourceCurrency);

    return this.formatPrice(convertedAmount, targetCurrency);
  }

  convertPrice(amount, sourceCurrency, targetCurrency) {
    const shopCurrency = this.dataset.shopCurrency;
    const shopifyCurrency = window.Shopify?.currency;
    const shopifyRate = Number.parseFloat(shopifyCurrency?.rate);

    if (
      sourceCurrency === shopCurrency &&
      shopifyCurrency?.active === targetCurrency &&
      Number.isFinite(shopifyRate)
    ) {
      return amount * shopifyRate;
    }

    if (typeof window.Currency?.convert === 'function') {
      try {
        const convertedAmount = Number.parseFloat(window.Currency.convert(amount, sourceCurrency, targetCurrency));
        if (Number.isFinite(convertedAmount)) return convertedAmount;
      } catch (error) {
        return null;
      }
    }

    return null;
  }

  formatPrice(value, currency) {
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return `${value} ${currency || ''}`.trim();

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2)} ${currency || ''}`.trim();
    }
  }

  setLoading(isLoading) {
    this.button.disabled = isLoading;
    this.button.setAttribute('aria-busy', String(isLoading));
    this.buttonLabel.textContent = isLoading ? this.dataset.loadingLabel : this.defaultButtonLabel;
    this.status.textContent = isLoading ? this.dataset.loadingLabel : '';
  }

  clearFeedback() {
    this.status.textContent = '';
    this.error.textContent = '';
    this.error.hidden = true;
    this.results.hidden = true;
    this.rates.replaceChildren();
  }

  showError() {
    this.status.textContent = '';
    this.error.textContent = this.dataset.errorText;
    this.error.hidden = false;
  }
}

if (!customElements.get('shipping-estimator')) {
  customElements.define('shipping-estimator', CartShippingEstimator);
}
})();
